import { Guid } from "guid-typescript";

import ServerError from "../types/system/ServerError";
import NotifiablePromise from "../types/system/NotifiablePromise{}";
import AppState from "../foundation/AppState";
import { handleError } from "../state/system/Alerts";
import store from "../state/Store";
import Utilities from "../foundation/Utility";

export interface RequestOptions {
    method: string;
    headers?: Record<string, string>;
    body?: any;
    external?: boolean;
    params?: Record<string, any>;
}

class WebRequest {
    protected static get includeAuth(): boolean {
        return false;
    }

    protected static get basePath(): string {
        return "";
    }

    private static fetch(
        path: string,
        requestOptions: RequestOptions,
    ): NotifiablePromise<any> {
        return new NotifiablePromise<any>(
            async (resolve, reject, notify, initialise) => {
                try {
                    let client = new XMLHttpRequest();

                    initialise(client);

                    if (
                        requestOptions?.params &&
                        (requestOptions?.method ?? "GET") === "GET"
                    ) {
                        const params = Object.entries(requestOptions.params)
                            .map(
                                entry =>
                                    `${encodeURIComponent(
                                        entry[0],
                                    )}=${encodeURIComponent(
                                        JSON.stringify(entry[1]),
                                    )}`,
                            )
                            .join("&");

                        if (path.includes("?")) {
                            path += `&${params}`;
                        } else {
                            path += `?${params}`;
                        }
                    }

                    client.open(
                        requestOptions?.method ?? "GET",
                        `${this.basePath}${path}`,
                    );

                    if (this.includeAuth) {
                        const jwt = await AppState.instance.getSessionJWT();
                        if (!jwt) {
                            throw new Error("No authentication found");
                        }

                        requestOptions.headers[
                            "Authorization"
                        ] = `Bearer ${jwt}`;
                    }

                    if (requestOptions?.headers) {
                        if (
                            requestOptions?.headers?.["Request-Type"] === "blob"
                        ) {
                            client.responseType = "blob";
                            delete requestOptions.headers["Request-Type"];
                        }
                        Object.entries(requestOptions.headers).forEach(entry =>
                            client.setRequestHeader(entry[0], `${entry[1]}`),
                        );
                    }

                    client.upload.addEventListener(
                        "progress",
                        (progressEvent: ProgressEvent<EventTarget>) => {
                            notify(progressEvent);
                        },
                    );

                    client.addEventListener(
                        "loadend",
                        (progressEvent: ProgressEvent<EventTarget>) => {
                            notify(progressEvent);
                        },
                    );

                    client.addEventListener("load", () => {
                        switch (client.status) {
                            case 401:
                                AppState.reset();
                                document.location = `${document.location.protocol}//${document.location.host}${document.location.pathname}`;
                                break;
                            case 406:
                            case 500:
                                let error = JSON.parse(
                                    client.responseText,
                                ) as ServerError;
                                error.message = error.exceptionMessage;
                                store.dispatch(handleError(error));
                                reject(error);
                        }

                        resolve(client.response);
                    });

                    client.addEventListener(
                        "timeout",
                        (progressEvent: ProgressEvent<EventTarget>) => {
                            notify(progressEvent);
                            notify(progressEvent);

                            reject({
                                message: `Server request timeout: ${path}`,
                                exceptionMessage: `Server request timeout: ${path}`,
                                id: Guid.create().toString(),
                                reason: "Server request error",
                            } as ServerError);
                        },
                    );

                    client.addEventListener(
                        "abort",
                        (progressEvent: ProgressEvent<EventTarget>) => {
                            notify(progressEvent);
                            notify(progressEvent);

                            reject({
                                message: `Server request aborted: ${path}`,
                                exceptionMessage: `Server request aborted: ${path}`,
                                id: Guid.create().toString(),
                                reason: "Server request error",
                            } as ServerError);
                        },
                    );

                    client.addEventListener(
                        "error",
                        (progressEvent: ProgressEvent<EventTarget>) => {
                            notify(progressEvent);

                            switch (client.status) {
                                case 401:
                                    AppState.reset();
                                    document.location = `${document.location.protocol}//${document.location.host}${document.location.pathname}`;
                                    break;
                                default:
                                    let error = JSON.parse(
                                        client.responseText,
                                    ) as ServerError;
                                    error.message = error.exceptionMessage;
                                    store.dispatch(
                                        handleError({
                                            message:
                                                "Unable to connect to the server. Please check your internet connection and try again.",
                                            id: "ConnectionError",
                                            reason: "Connection Error",
                                        }),
                                    );
                                    reject(error);
                            }
                        },
                    );

                    client.send(
                        requestOptions.body
                            ? JSON.stringify(requestOptions.body)
                            : null,
                    );
                } catch (error) {
                    store.dispatch(handleError(error));

                    reject({
                        message: error,
                        exceptionMessage: error,
                        id: Guid.create().toString(),
                        reason: "Client error",
                    } as ServerError);
                }
            },
        );
    }
    private static fetchType<T>(
        path: string,
        requestOptions: RequestOptions,
    ): NotifiablePromise<any> {
        return new NotifiablePromise<T>(
            (resolve, reject, notify, initialise) => {
                let promise = this.fetch(path, requestOptions)
                    .progress(notify)
                    .then(result => {
                        resolve(result ? (JSON.parse(result) as T) : undefined);
                    }, reject);

                initialise(promise.data);
            },
        );
    }
    static raw(
        path: string,
        requestOptions?: RequestOptions,
    ): NotifiablePromise<Blob> {
        requestOptions = requestOptions ?? { method: "GET" };
        requestOptions.headers = {
            ...requestOptions.headers,
            "Content-Type": "application/json;charset=UTF-8",
            "Request-Type": "blob",
        };
        return this.fetch(path, requestOptions);
    }
    static get(
        path: string,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<any> {
        requestOptions = requestOptions ?? { method: "GET" };
        return this.fetch(path, {
            ...requestOptions,
            headers: {
                ...requestOptions.headers,
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "GET",
        });
    }
    static getType<T>(
        path: string,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<T> {
        requestOptions = requestOptions ?? { method: "GET" };
        return this.fetchType<T>(path, {
            ...(requestOptions ?? {}),
            headers: {
                ...(requestOptions?.headers ?? {}),
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "GET",
        });
    }
    static getPost(
        path: string,
        data: Record<string, any>,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<any> {
        return this.fetch(path, {
            ...(requestOptions ?? {}),
            headers: {
                ...(requestOptions?.headers ?? {}),
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "GET",
            params: data,
        });
    }
    static getPostType<T>(
        path: string,
        data: Record<string, any>,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<T> {
        return this.fetchType<T>(path, {
            ...(requestOptions ?? {}),
            headers: {
                ...(requestOptions?.headers ?? {}),
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "GET",
            params: data,
        });
    }
    static post(
        path: string,
        data?: any,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<any> {
        return this.fetch(path, {
            ...(requestOptions ?? {}),
            headers: {
                ...(requestOptions?.headers ?? {}),
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "POST",
            body: data ?? null,
        });
    }
    static postType<T>(
        path: string,
        data?: any,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<T> {
        return this.fetchType<T>(path, {
            ...(requestOptions ?? {}),
            headers: {
                ...(requestOptions?.headers ?? {}),
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "POST",
            body: data ?? null,
        });
    }
    static put(
        path: string,
        data?: any,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<any> {
        return this.fetch(path, {
            ...(requestOptions ?? {}),
            headers: {
                ...(requestOptions?.headers ?? {}),
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "PUT",
            body: data ?? null,
        });
    }
    static putType<T>(
        path: string,
        data?: any,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<T> {
        return this.fetchType<T>(path, {
            ...(requestOptions ?? {}),
            headers: {
                ...(requestOptions?.headers ?? {}),
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "PUT",
            body: data ?? null,
        });
    }
    static delete(
        path: string,
        data?: any,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<any> {
        return this.fetch(path, {
            ...(requestOptions ?? {}),
            headers: {
                ...(requestOptions?.headers ?? {}),
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "DELETE",
            body: data ?? null,
        });
    }
    static deleteType<T>(
        path: string,
        data?: any,
        requestOptions?: Partial<RequestOptions>,
    ): NotifiablePromise<T> {
        return this.fetchType<T>(path, {
            ...(requestOptions ?? {}),
            headers: {
                ...(requestOptions?.headers ?? {}),
                "Content-Type": "application/json;charset=UTF-8",
            },
            method: "DELETE",
            body: data ?? null,
        });
    }
}

export default WebRequest;
